import React from "react";
import { Navigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import Unauthorized from "../components/Unauthorized";

/**
 * Map legacy `allowedRoles` strings (the pre-refactor role enum) onto the new
 * PersonaType. Multiple legacy roles fold into a single persona — e.g. ADMIN /
 * SALES / PRESALES / PRESALES_SALES are all "STAFF" now.
 */
const LEGACY_ROLE_TO_PERSONA = {
  // Legacy "ADMIN" used to mean "developer-side admin staff". In the new model
  // the org's primary admin login is DEVELOPER_ADMIN persona; granular staff is STAFF.
  ADMIN: ["STAFF", "DEVELOPER_ADMIN"],
  DEVELOPER: ["DEVELOPER_ADMIN"],
  SALES: ["STAFF"],
  PRESALES: ["STAFF"],
  PRESALES_SALES: ["STAFF"],
  BV_USER: ["BV_ASSOCIATE"],
  USER: ["PROPERTY_BUYER"],
  AGENT: ["CHANNEL_PARTNER"],
  INTERIOR_DESIGNER: ["INTERIOR_DESIGNER"],
  ZUMALE_USER: ["ZUMALE_USER"],
  ARCHITECT: ["ARCHITECT"],
  FREELANCER: ["FREELANCER"],
};

function normalizeRoleName(roleName) {
  if (!roleName || typeof roleName !== "string") return "";
  return roleName.trim().toUpperCase().replace(/-/g, "_");
}

function PrivateRoute({ children, allowedRoles }) {
  const { userInfo, loading } = useUser();

  if (loading) {
    return <div>Loading....</div>;
  }
  if (!userInfo) {
    return <Navigate to="/" />;
  }

  const userPersona = userInfo?.persona || "";
  const normalizedAllowed = Array.isArray(allowedRoles)
    ? allowedRoles.map(normalizeRoleName)
    : [];

  // Build the set of personas accepted by this route based on the legacy
  // allowedRoles list (still used by many of the existing route entries).
  const allowedPersonas = new Set();
  for (const legacy of normalizedAllowed) {
    const mapped = LEGACY_ROLE_TO_PERSONA[legacy];
    if (mapped) {
      mapped.forEach((p) => allowedPersonas.add(p));
    }
  }

  const userHasRequiredRole =
    normalizedAllowed.length > 0 && allowedPersonas.has(userPersona);

  if (!userHasRequiredRole) {
    return <Unauthorized />;
  }
  return children;
}

export default PrivateRoute;
